import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileDown, FileSpreadsheet, FileText, TrendingDown, TrendingUp } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { downloadFile, errMsg } from "@/lib/api";
import { useApi, useClientOptions } from "@/hooks/useApi";
import { PageHeader, Section, ErrorBox, Loading, FilterBar, DataTable, StatusBadge } from "@/components/common/ui-bits";
import { DateInput } from "@/components/common/DateInput";
import { money, fmtDate, todayISO } from "@/lib/format";

const COLORS = { "Planes LLC": "#7C3AED", Taxes: "#0F766E", "Presencia digital": "#1D4ED8", Mantenimiento: "#D97706" };
const PIE = ["#1D4ED8", "#0F766E", "#D97706", "#7C3AED", "#DB2777", "#475569", "#0891B2"];
const LINES = Object.keys(COLORS);

const shift = (iso, months) => { const [y, m] = iso.split("-").map(Number); const d = new Date(Date.UTC(y, m - 1 + months, 1)); return d.toISOString().slice(0, 10); };
const lastDay = (first) => { const d = new Date(`${shift(first, 1)}T00:00:00Z`); d.setUTCDate(0); return d.toISOString().slice(0, 10); };
const presets = () => {
  const t = todayISO(); const m1 = `${t.slice(0, 7)}-01`;
  return [
    { key: "month", label: "Este mes", from: m1, to: t },
    { key: "prev", label: "Mes anterior", from: shift(m1, -1), to: lastDay(shift(m1, -1)) },
    { key: "q", label: "Últimos 3 meses", from: shift(m1, -2), to: t },
    { key: "year", label: "Este año", from: `${t.slice(0, 4)}-01-01`, to: t },
    { key: "12m", label: "Últimos 12 meses", from: shift(m1, -11), to: t },
  ];
};

function Kpi({ label, value, hint, testid, delta }) {
  return (
    <div className="card-box fade-up p-5" data-testid={testid}>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="num mt-2 text-2xl font-semibold text-slate-900" data-testid={`${testid}-value`}>{value}</p>
      {delta != null && <p className={`mt-1 flex items-center gap-1 text-xs font-semibold ${delta >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid={`${testid}-delta`}>
        {delta >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}{delta > 0 ? "+" : ""}{delta}% vs. periodo anterior</p>}
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}

const tip = (v) => money(v);

function MonthlyChart({ data }) {
  return (
    <Section title="Ingresos cobrados por mes" testid="chart-monthly">
      <div className="h-72"><ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ left: 8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="label" tick={{ fontSize: 12 }} /><YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${v}`} width={60} />
          <Tooltip formatter={tip} /><Legend />
          {LINES.map((l) => <Bar key={l} dataKey={l} stackId="a" fill={COLORS[l]} radius={l === "Mantenimiento" ? [4, 4, 0, 0] : 0} />)}
        </BarChart>
      </ResponsiveContainer></div>
    </Section>
  );
}

function Donut({ title, data, testid }) {
  return (
    <Section title={title} testid={testid}>
      {!data.length ? <p className="py-10 text-center text-sm text-slate-400">Sin pagos en el periodo.</p> : (
        <div className="h-64"><ResponsiveContainer width="100%" height="100%">
          <PieChart><Pie data={data} dataKey="amount" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
            {data.map((d, i) => <Cell key={d.name} fill={COLORS[d.name] || PIE[i % PIE.length]} />)}</Pie>
            <Tooltip formatter={tip} /><Legend /></PieChart>
        </ResponsiveContainer></div>)}
    </Section>
  );
}

function Recurring({ rc }) {
  return (
    <Section title="Ingreso recurrente (renovaciones registradas)" className="mt-6" testid="recurring-section">
      <div className="mb-4 grid gap-4 sm:grid-cols-3">
        <Kpi label="MRR (mensual)" value={money(rc.mrr)} hint={`${rc.services} servicio(s) con precio${rc.unpriced ? ` · ${rc.unpriced} con precio [PENDIENTE]` : ""}`} testid="kpi-mrr" />
        <Kpi label="ARR (anual)" value={money(rc.arr)} hint="MRR × 12" testid="kpi-arr" />
        <Kpi label="Potencial: vencidas y próximos 90 días" value={money(rc.potential_90d)} hint={rc.potential_90d_unpriced ? `+ ${rc.potential_90d_unpriced} renovación(es) con precio [PENDIENTE]` : "Solo cuenta como ingreso al cobrarse"} testid="kpi-potential" />
      </div>
      <DataTable testid="recurring-upcoming-table" rows={rc.upcoming} empty="No hay renovaciones en los próximos 90 días."
        columns={[{ key: "client_name", label: "Cliente" }, { key: "service_type", label: "Servicio", render: (r) => `${r.service_type}: ${r.name}` },
          { key: "expiry_date", label: "Vence", render: (r) => fmtDate(r.expiry_date) }, { key: "frequency", label: "Frecuencia" },
          { key: "client_price", label: "Precio", render: (r) => <span className="num">{money(r.client_price)}</span> },
          { key: "status", label: "Estado", render: (r) => <StatusBadge status={r.status} /> }]} />
    </Section>
  );
}

function Breakdown({ title, rows, testid }) {
  return (
    <Section title={title} testid={testid}>
      {!rows.length ? <p className="text-sm text-slate-400">Sin datos.</p> :
        <ul className="divide-y divide-slate-100 text-sm">{rows.map((r) => <li key={r.name} className="flex justify-between gap-3 py-2"><span className="truncate">{r.name}</span><span className="num font-semibold">{money(r.amount)}</span></li>)}</ul>}
    </Section>
  );
}

export default function Reports() {
  const list = presets();
  const [range, setRange] = useState({ preset: "year", from: list[3].from, to: list[3].to });
  const [client, setClient] = useState({});
  const clients = useClientOptions();
  const params = { date_from: range.from, date_to: range.to, ...(client.client_id ? { client_id: client.client_id } : {}) };
  const { data, loading, error } = useApi("/reports/revenue", params);
  const [busy, setBusy] = useState("");
  const exp = async (format) => {
    setBusy(format);
    try { await downloadFile("/reports/revenue/export", { ...params, format }, `Reporte-ingresos.${format}`); toast.success("Archivo descargado"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const k = data?.kpis;

  return (
    <div>
      <PageHeader title="Reportes de ingresos" subtitle="Solo los pagos recibidos cuentan como ingreso. Las facturas pendientes se muestran aparte."
        actions={<>
          <Button variant="outline" disabled={!!busy} onClick={() => exp("pdf")} data-testid="export-pdf-button"><FileText className="mr-2 h-4 w-4" />PDF</Button>
          <Button variant="outline" disabled={!!busy} onClick={() => exp("csv")} data-testid="export-csv-button"><FileDown className="mr-2 h-4 w-4" />CSV</Button>
          <Button variant="outline" disabled={!!busy} onClick={() => exp("xlsx")} data-testid="export-xlsx-button"><FileSpreadsheet className="mr-2 h-4 w-4" />Excel</Button>
        </>} />
      <div className="mb-4 flex flex-wrap gap-2" data-testid="report-presets">
        {list.map((p) => <Button key={p.key} size="sm" variant={range.preset === p.key ? "default" : "outline"} onClick={() => setRange({ preset: p.key, from: p.from, to: p.to })} data-testid={`preset-${p.key}`}>{p.label}</Button>)}
      </div>
      <div className="mb-6 flex flex-wrap items-end gap-3">
        <div className="w-44"><label className="field-label">Desde</label><DateInput value={range.from} onChange={(v) => v && setRange({ ...range, preset: "", from: v })} testid="report-from" clearable={false} /></div>
        <div className="w-44"><label className="field-label">Hasta</label><DateInput value={range.to} onChange={(v) => v && setRange({ ...range, preset: "", to: v })} testid="report-to" clearable={false} /></div>
        <FilterBar values={client} onChange={setClient} testid="report-filters" filters={[{ key: "client_id", label: "Cliente", options: clients }]} />
      </div>
      <ErrorBox text={error} />
      {error ? null : !data ? (loading ? <Loading /> : null) : <>
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Kpi label="Cobrado en el periodo" value={money(k.collected)} delta={k.change_pct} hint={`${k.payments} pago(s) · anterior ${money(k.prev_collected)}`} testid="kpi-collected" />
          <Kpi label="Facturado en el periodo" value={money(k.invoiced)} hint={`${k.invoices_issued} factura(s) emitidas`} testid="kpi-invoiced" />
          <Kpi label="Pendiente de cobro" value={money(k.outstanding)} hint={`${k.outstanding_count} factura(s) · vencido ${money(k.overdue)}`} testid="kpi-outstanding" />
          <Kpi label="Reembolsado" value={money(k.refunded)} hint={`Pago promedio ${money(k.avg_payment)}`} testid="kpi-refunded" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2"><MonthlyChart data={data.monthly} /></div>
          <Donut title="Por línea de negocio" data={data.by_line} testid="chart-by-line" />
        </div>
        <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          <Breakdown title="Por categoría" rows={data.by_category} testid="breakdown-category" />
          <Breakdown title="Por tipo de factura" rows={data.by_type} testid="breakdown-type" />
          <Breakdown title="Por método de pago" rows={data.by_method} testid="breakdown-method" />
          <Breakdown title="Principales clientes" rows={data.top_clients} testid="breakdown-clients" />
        </div>
        <Recurring rc={data.recurring} />
      </>}
    </div>
  );
}
